import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isInApp } from '../../utils/isInApp';
import AppPaymentBlocker from '../../components/AppPaymentBlocker';
import { useToast } from '../../components/Toast';

const BillingKeyRegistration = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [tossPayments, setTossPayments] = useState(null);
  
  // API 기본 URL 설정
  const getApiBaseUrl = () => {
    const currentUrl = window.location.hostname;
    if (currentUrl.includes('localhost')) {
      return 'http://localhost:8000';
    } else if (currentUrl.includes('whatquiztest')) {
      return 'https://quiz-api-test-397277209875.us-central1.run.app';
    } else {
      return 'https://quiz-api-397277209875.us-central1.run.app';
    }
  };

  // 토스 페이먼츠 SDK 로드 및 초기화
  useEffect(() => {
    const loadTossPaymentsSDK = () => {
      return new Promise((resolve, reject) => {
        if (window.TossPayments) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://js.tosspayments.com/v1/payment';
        script.async = true;
        
        script.onload = () => {
          resolve();
        };
        
        script.onerror = () => {
          console.error('토스 페이먼츠 SDK 로드 실패');
          reject(new Error('토스 페이먼츠 SDK 로드 실패'));
        };
        
        document.head.appendChild(script);
      });
    };

    const initializeTossPayments = async () => {
      if (!currentUser) return;

      try {
        // SDK 로드
        await loadTossPaymentsSDK();

        // 백엔드에서 클라이언트 키 가져오기
        const API_BASE_URL = getApiBaseUrl();
        const idToken = await currentUser.getIdToken(true);
        
        const response = await fetch(`${API_BASE_URL}/api/payment/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({
            plan_id: 'pro_3month'
          })
        });

        if (!response.ok) {
          throw new Error('결제 정보를 불러오지 못했어요.');
        }

        const result = await response.json();
        const { toss_config } = result.data;
        
        // 토스 페이먼츠 초기화
        const tossPaymentsInstance = window.TossPayments(toss_config.client_key);
        setTossPayments(tossPaymentsInstance);
        
      } catch (error) {
        console.error('토스 페이먼츠 초기화 실패:', error);
        showToast('결제 화면을 준비하지 못했어요. 새로고침 후 다시 시도해주세요', 'error', 5000);
      }
    };

    initializeTossPayments();
  }, [currentUser, showToast]);

  // 빌링키 발급 요청
  const handleBillingKeyRegistration = async () => {
    if (!currentUser) {
      showToast('로그인하면 카드를 등록할 수 있어요', 'info');
      navigate('/login');
      return;
    }

    if (!tossPayments) {
      showToast('결제 화면을 준비하고 있어요. 잠시 후 다시 시도해주세요', 'info');
      return;
    }

    setIsLoading(true);

    try {
      // customerKey 생성 (UUID 사용)
      const customerKey = `WHATQUIZ_${currentUser.uid}_${Date.now()}`;
      
      // 빌링키 발급 요청 (토스 페이먼츠 공식 가이드 방식)
      await tossPayments.requestBillingAuth('카드', {
        customerKey: customerKey,
        successUrl: `${window.location.origin}/billing/success`,
        failUrl: `${window.location.origin}/billing/fail`
      });

    } catch (error) {
      console.error('빌링키 발급 요청 중 오류:', error);
      
      if (error.code === 'USER_CANCEL') {
        showToast('카드 등록을 취소했어요', 'info');
      } else {
        showToast('카드를 등록하지 못했어요. 잠시 후 다시 시도해주세요', 'error', 5000);
      }
      
      setIsLoading(false);
    }
  };

  if (isInApp()) {
    return (
      <AppPaymentBlocker
        pageName="결제 수단 등록"
        webUrl="https://whatquiz.co.kr/billing-key-registration"
        fallbackPath="/pricing"
      />
    );
  }

  // 로그인 체크
  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={styles.loginPrompt}>
          <h2 style={styles.title}>로그인이 필요해요</h2>
          <p style={styles.description}>
            로그인하면 결제 카드를 등록할 수 있어요.
          </p>
          <button 
            style={styles.loginButton}
            onClick={() => navigate('/login')}
          >
            로그인하기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.pageTitle}>결제 수단 등록</h1>
        <p style={styles.pageSubtitle}>
          PRO 이용권 결제를 위한 카드를 등록해주세요
        </p>

        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>카드 등록 안내</h3>
          <ul style={styles.infoList}>
            <li>• 등록된 카드로 선택한 기간 동안 PRO 서비스를 이용할 수 있습니다</li>
            <li>• 국내 발급 신용/체크카드만 등록 가능합니다</li>
            <li>• 카드 정보는 토스 페이먼츠에서 안전하게 관리됩니다</li>
            <li>• 언제든지 등록된 카드를 변경할 수 있습니다</li>
          </ul>
        </div>

        <div style={styles.securityInfo}>
          <div style={styles.securityIcon}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </div>
          <div>
            <h4 style={styles.securityTitle}>안전한 결제</h4>
            <p style={styles.securityText}>
              카드 정보는 PCI-DSS 인증을 받은 토스 페이먼츠에서 안전하게 암호화되어 저장됩니다.
            </p>
          </div>
        </div>

        <button
          style={{
            ...styles.registerButton,
            ...(isLoading ? styles.registerButtonDisabled : {})
          }}
          onClick={handleBillingKeyRegistration}
          disabled={isLoading}
        >
          {isLoading ? '처리 중...' : '카드 등록하기'}
        </button>

        <p style={styles.helpText}>
          카드 등록 시 본인인증이 필요합니다
        </p>
      </div>
    </div>
  );
};

// 스타일 정의
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '2rem 1rem'
  },
  content: {
    maxWidth: '600px',
    margin: '0 auto'
  },
  pageTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a202c',
    textAlign: 'center',
    marginBottom: '0.5rem'
  },
  pageSubtitle: {
    fontSize: '1.1rem',
    color: '#718096',
    textAlign: 'center',
    marginBottom: '3rem'
  },
  infoCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  infoTitle: {
    fontSize: '1.3rem',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '1rem'
  },
  infoList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    color: '#4a5568',
    lineHeight: '1.8'
  },
  securityInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    backgroundColor: '#e6f4ff',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '2rem'
  },
  securityIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    color: '#3182f6'
  },
  securityTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '0.25rem'
  },
  securityText: {
    fontSize: '0.95rem',
    color: '#4a5568',
    margin: 0
  },
  registerButton: {
    width: '100%',
    padding: '1rem',
    backgroundColor: '#3182f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '1rem'
  },
  registerButtonDisabled: {
    backgroundColor: '#cbd5e0',
    cursor: 'not-allowed'
  },
  helpText: {
    textAlign: 'center',
    color: '#718096',
    fontSize: '0.9rem'
  },
  loginPrompt: {
    maxWidth: '400px',
    margin: '80px auto',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    padding: '48px 32px',
    borderRadius: '20px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#191f28',
    margin: '0 0 12px'
  },
  description: {
    fontSize: '15px',
    color: '#6b7684',
    margin: '0 0 24px'
  },
  loginButton: {
    padding: '14px 32px',
    backgroundColor: '#3182f6',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  }
};

export default BillingKeyRegistration; 